import "./editor.css";
import type { Editor } from "@tiptap/core";
import { useEditor, EditorContent } from "@tiptap/react";
import { useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Image from "@tiptap/extension-image";
import CharacterCount from "@tiptap/extension-character-count";
import Typography from "@tiptap/extension-typography";
import Link from "@tiptap/extension-link";
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { TextAlign } from "@tiptap/extension-text-align";
import { Superscript } from "@tiptap/extension-superscript";
import { Subscript } from "@tiptap/extension-subscript";
import { Highlight } from "@tiptap/extension-highlight";
import { common, createLowlight } from "lowlight";
import { Markdown } from "tiptap-markdown";
import { useEffect, useEffectEvent, useState } from "react";
import { Maximize2, X, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { EditorToolbar } from "./EditorToolbar";
import { BubbleToolbar } from "./BubbleToolbar";
import { SlashCommands } from "./SlashCommandMenu";
import { VideoEmbed, TweetEmbed, Callout, Footnote, FileAttachment } from "./extensions";
import { TableOfContents } from "./TableOfContents";
import { ReadingTime } from "./ReadingTime";

// tiptap-markdown doesn't augment @tiptap/core's Storage interface, so this
// declares what it stores.
declare module "@tiptap/core" {
  interface Storage {
    markdown: { getMarkdown(): string };
  }
}

function getMarkdown(editor: Editor): string {
  return editor.storage.markdown.getMarkdown();
}

const lowlight = createLowlight(common);

interface TiptapEditorProps {
  content?: string;
  onChange?: (markdown: string) => void;
  placeholder?: string;
  minHeight?: string;
  characterLimit?: number;
  showWordCount?: boolean;
  showTableOfContents?: boolean;
  className?: string;
}

export function TiptapEditor({
  content = "",
  onChange,
  placeholder = "Start writing...",
  minHeight = "200px",
  characterLimit,
  showWordCount = false,
  showTableOfContents = false,
  className = "",
}: TiptapEditorProps) {
  const [focusMode, setFocusMode] = useState(false);
  const [tocCollapsed, setTocCollapsed] = useState(true);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false,
        heading: { levels: [1, 2, 3] },
      }),
      Placeholder.configure({
        placeholder: ({ node, editor: e }) => {
          // Only show placeholder when the entire document is empty
          if (e.state.doc.childCount > 1 || e.state.doc.textContent.length > 0) return "";
          if (node.type.name === "heading") return "Heading...";
          return placeholder;
        },
        showOnlyCurrent: true,
      }),
      Image.configure({
        inline: false,
        HTMLAttributes: { class: "editor-image" },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: "editor-link" },
      }),
      Typography,
      CodeBlockLowlight.configure({ lowlight }),
      // Tables
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      // Text formatting
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Superscript,
      Subscript,
      Highlight,
      // Custom extensions
      VideoEmbed,
      TweetEmbed,
      Callout,
      Footnote,
      FileAttachment,
      // Slash commands
      SlashCommands,
      // Markdown I/O
      Markdown.configure({
        html: true,
        tightLists: true,
        bulletListMarker: "-",
        transformPastedText: true,
      }),
      // Character count
      ...(characterLimit
        ? [CharacterCount.configure({ limit: characterLimit })]
        : [CharacterCount]),
    ],
    content,
    editorProps: {
      attributes: {
        class: "tiptap-editor focus:outline-none",
        style: `min-height: ${minHeight}`,
      },
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files;
        if (!files?.length) return false;

        event.preventDefault();
        const file = files[0];

        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onload = () => {
            const { state } = view;
            const pos = view.posAtCoords({
              left: event.clientX,
              top: event.clientY,
            })?.pos;
            if (pos == null) return;
            const node = state.schema.nodes.image.create({
              src: reader.result as string,
            });
            const tr = state.tr.insert(pos, node);
            view.dispatch(tr);
          };
          reader.readAsDataURL(file);
          return true;
        }

        return false;
      },
      handlePaste: (_view, event) => {
        const files = event.clipboardData?.files;
        if (!files?.length) return false;

        const file = files[0];
        if (file.type.startsWith("image/")) {
          event.preventDefault();
          const reader = new FileReader();
          reader.onload = () => {
            editor?.chain().focus().setImage({ src: reader.result as string }).run();
          };
          reader.readAsDataURL(file);
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      onChange?.(getMarkdown(editor));
    },
  });

  const setContentFromParent = useEffectEvent((newContent: string) => {
    if (!editor) return;
    const currentMd = getMarkdown(editor);
    if (newContent !== currentMd) {
      editor.commands.setContent(newContent);
    }
  });

  useEffect(() => {
    setContentFromParent(content);
  }, [content]);

  const counts = useEditorState({
    editor,
    selector: ({ editor }) => ({
      characters: editor.storage.characterCount.characters(),
      words: editor.storage.characterCount.words(),
    }),
  });

  // Body scroll lock + Escape key handler for focus mode
  useEffect(() => {
    if (!focusMode) return;

    document.body.style.overflow = "hidden";

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented) {
        setFocusMode(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [focusMode]);

  if (!editor) return null;

  return (
    <div
      className={
        focusMode
          ? `tiptap-wrapper fixed inset-0 z-50 flex flex-col bg-cream`
          : `tiptap-wrapper rounded-lg border border-beige/40 bg-white overflow-hidden ${className}`
      }
      data-focus-mode={focusMode || undefined}
    >
      {/* Focus mode header */}
      {focusMode && counts && (
        <div className="flex items-center justify-between px-6 py-3 border-b border-beige/20">
          <span className="font-body text-sm text-grey">Focus Mode</span>
          <div className="flex items-center gap-4">
            <span className="font-body text-xs text-grey">
              {counts.words} words
            </span>
            <span className="font-body text-xs text-beige">|</span>
            <span className="font-body text-xs text-grey">
              {counts.characters} chars
            </span>
            <span className="font-body text-xs text-beige">|</span>
            <ReadingTime words={counts.words} />
            <button
              type="button"
              onClick={() => setFocusMode(false)}
              title="Exit Focus Mode (Esc)"
              className="p-1.5 rounded-lg text-grey hover:bg-beige/15 hover:text-black transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      )}

      <EditorToolbar editor={editor} />
      <BubbleToolbar editor={editor} />

      <div className={focusMode ? "flex-1 overflow-y-auto" : ""}>
        <div className={focusMode ? "max-w-3xl mx-auto relative" : "flex"}>
          {/* TOC sidebar — normal mode: inward-expanding */}
          {showTableOfContents && !focusMode && (
            <div
              className={`hidden lg:block shrink-0 border-r border-beige/20 overflow-hidden transition-all duration-200 ease-in-out ${
                tocCollapsed ? "w-8" : "w-48"
              }`}
            >
              <div className="sticky top-0 max-h-[600px] overflow-y-auto">
                <button
                  type="button"
                  onClick={() => setTocCollapsed((c) => !c)}
                  title={tocCollapsed ? "Show contents" : "Hide contents"}
                  className="p-2 text-beige hover:text-grey transition-colors"
                >
                  {tocCollapsed ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
                </button>
                {!tocCollapsed && (
                  <div className="px-3 pb-3">
                    <TableOfContents editor={editor} />
                  </div>
                )}
              </div>
            </div>
          )}
          {/* TOC sidebar — focus mode: outward-expanding */}
          {showTableOfContents && focusMode && (
            <div className="hidden lg:block absolute right-full top-0 h-full">
              <div className="sticky top-0 flex items-start">
                <div
                  className={`overflow-hidden transition-all duration-200 ease-in-out ${
                    tocCollapsed ? "w-0 opacity-0" : "w-48 opacity-100"
                  }`}
                >
                  <div className="w-48 p-3 max-h-[600px] overflow-y-auto">
                    <TableOfContents editor={editor} />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setTocCollapsed((c) => !c)}
                  title={tocCollapsed ? "Show contents" : "Hide contents"}
                  className="p-2 text-beige hover:text-grey transition-colors"
                >
                  {tocCollapsed ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
                </button>
              </div>
            </div>
          )}
          <div className="flex-1">
            <EditorContent editor={editor} />
          </div>
        </div>
      </div>

      {/* Normal mode footer */}
      {!focusMode && showWordCount && counts && (
        <div className="flex items-center justify-between px-4 py-2 border-t border-beige/20 text-xs font-body text-grey">
          <div className="flex items-center gap-3">
            <span>{counts.words} words</span>
            <span className="text-beige">|</span>
            <span>
              {counts.characters}
              {characterLimit ? ` / ${characterLimit}` : ""} chars
            </span>
            <span className="text-beige">|</span>
            <ReadingTime words={counts.words} />
          </div>
          <button
            type="button"
            onClick={() => setFocusMode(true)}
            title="Focus Mode"
            className="p-1 rounded text-grey hover:bg-beige/15 hover:text-black transition-colors"
          >
            <Maximize2 size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
